import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminPaginatedEnvelope,
  AfriexCountry,
  AfriexCurrencyCode,
  AfriexQueryParams,
  AfriexTransactionProcessor,
  AfriexTransactionType,
} from '@/types';

const commonEndpoint = '/v2/admin/countries';

export type CountryResponse = AdminPaginatedEnvelope<AfriexCountry[]>;

export type UpdateProcessorRequest = {
  currency: AfriexCurrencyCode;
  type: AfriexTransactionType;
  paymentMethodName: AfriexTransactionProcessor;
};

export const countryService = {
  getList: async (filters: AfriexQueryParams): Promise<CountryResponse> => {
    const params = {
      page: 0,
      limit: 20,
      columns: [
        'name',
        'iso2',
        'flag',
        'status',
        'currency',
        'idOptions',
        // Requested as exact dot paths rather than a bare `params`, so the
        // Mongo projection stays narrow and the boundary filter matches the
        // whitelist entries exactly instead of expanding the whole subtree.
        // Both feed the Settings -> Countries expanded row, which shows a
        // country's KYC provider and referral terms without a second
        // round-trip per row.
        'params.kycProcessor',
        'params.referralParams',
        // Bare, unlike the two above: the whitelist names the four permission
        // leaves the expanded row's pills render, and a top-level requested
        // column expands to exactly those. The Mongo projection takes the
        // small fixed-key subtree whole and the boundary narrows it.
        'permissions',
      ],
      ...filters,
    };
    const url = `${commonEndpoint}?${toQueryString(params)}`;
    const response = await axios.get<CountryResponse>(url);
    return response.data;
  },
  getItem: async (countryCode: string): Promise<AfriexCountry> => {
    const response = await axios.get<AfriexCountry>(
      commonEndpoint + `/${countryCode}`,
    );
    return response.data;
  },

  saveOne: async (
    itemId: string,
    body: Partial<AfriexCountry>,
  ): Promise<AfriexCountry> => {
    const response = await axios.patch<AfriexCountry>(
      commonEndpoint + `/${itemId}`,
      body,
    );
    return response.data;
  },
};
